import { CustomWorksheetTemplateOverview, CWTBehaviourEnum } from '../../../types/custom-worksheet-v2';
import { ButtonV2, Text, TooltipV2 } from '@cimplico/components';
import { CalculatorIcon, LinkIcon, PlusIcon, ShareIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { TagIcon } from '@heroicons/react/24/solid';
import { Calculation, Worksheet } from '../../../types/worksheet';

import { useCreateWorksheetCalculation } from '../../../queries/use-create-worksheet-calculation';
import plural from '../../../utilities/plural';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { toastNotification } from '../../../components/toast/Toast';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../intercom-events';

type TemplateCardProps = {
    template: CustomWorksheetTemplateOverview;
    worksheet: Worksheet;
    isSuggestion?: boolean;
    calculation?: Calculation;
};
function TemplateCard({ template, isSuggestion, worksheet, calculation }: TemplateCardProps): JSX.Element {
    const { mutate, isLoading } = useCreateWorksheetCalculation(worksheet.id);
    const { readOnly } = useWorkpaperContext();
    const { trackEvent } = useIntercom();
    const isDisabled = isLoading || readOnly;

    function add(): void {
        mutate(
            { templateId: template.id },
            {
                onSuccess: () => {
                    trackEvent(intercomEvents.calculation.created.event);
                },
                onError: (error) => {
                    toastNotification(
                        'error',
                        'Unable to add calculation to worksheet',
                        error.response?.data?.message ?? 'Unable to add calculation to worksheet. Something went wrong.'
                    );
                },
            }
        );
    }

    return (
        <div key={template.id} className="p-4 border rounded relative">
            <div className="flex flex-col h-full gap-2">
                <div className="flex items-center gap-1 shrink-0">
                    {isSuggestion ? (
                        <SparklesIcon className="h-4 w-4 text-yellow-600 shrink-0" />
                    ) : (
                        <CalculatorIcon className="h-4 w-4 text-green-600" />
                    )}
                    <Text className="text-sm font-bold text-gray-800 ml-1">{template.name}</Text>
                </div>
                {template.description && (
                    <Text className="text-xs text-gray-500" lines={2}>
                        {template.description}
                    </Text>
                )}
                <div className="flex items-start gap-2 border-t pt-2 mt-auto">
                    {/* Add tags */}
                    <div>
                        <div className="flex flex-wrap gap-1 items-center">
                            <TagIcon className="h-4 w-4 text-gray-600" />
                            {template.tags.slice(0, 1).map((tag) => (
                                <label key={tag} className="text-xs text-gray-500">
                                    {tag}
                                </label>
                            ))}
                            {template.tags.length > 1 && (
                                <span className="-mt-1">
                                    <TooltipV2 message={template.tags.slice(1)}>
                                        <label className="text-xs text-gray-800">+{template.tags.length - 1}</label>
                                    </TooltipV2>
                                </span>
                            )}
                        </div>
                        {calculation && template.behaviour === CWTBehaviourEnum.SHARE && (
                            <div className="flex items-center gap-1 mt-2">
                                <ShareIcon className="text-gray-600 h-4 w-4" />
                                <label className="text-xs text-gray-500">
                                    <TooltipV2 message={calculation.worksheets.map((ws) => ws.name)}>
                                        {calculation.worksheets.length} {plural(calculation.worksheets.length, 'worksheet')}
                                    </TooltipV2>
                                </label>
                            </div>
                        )}
                    </div>
                    <span className="ml-auto">
                        <ButtonV2
                            size="xs"
                            label="Add"
                            leftIcon={<PlusIcon />}
                            type="secondary"
                            onClick={add}
                            isLoading={isLoading}
                            isDisabled={isDisabled}
                        />
                    </span>
                </div>
            </div>
        </div>
    );
}

export default TemplateCard;
